export const HERO_COMPACT_ON = 56;

/**
 * Expand only near the top, far from ON, so crossing the compact threshold
 * cannot toggle back. Height may shrink; scrollTop is compensated separately.
 */
export const HERO_COMPACT_OFF = 2;

function asNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Sticky project header hysteresis: compact after scrolling past ON, expand only near top. */
export function nextHeroCompact(
  scrollY,
  currentlyCompact,
  compactOn = HERO_COMPACT_ON,
  compactOff = HERO_COMPACT_OFF
) {
  const y = asNumber(scrollY);
  const on = asNumber(compactOn, HERO_COMPACT_ON);
  const off = asNumber(compactOff, HERO_COMPACT_OFF);
  return currentlyCompact ? y > off : y > on;
}

/**
 * Compact must use the real (shorter) height. Expanded tracks the live measure.
 * Returning 0 clears any leftover inline min-height.
 */
export function nextHeroSlotMinHeight(_currentMinHeight, measuredHeight, isCompact) {
  if (isCompact) return 0;
  return asNumber(measuredHeight);
}

export function applyHeroSlotMinHeight(slotEl, minHeight) {
  if (!slotEl) return 0;
  const next = asNumber(minHeight);
  if (next <= 0) {
    slotEl.style.minHeight = "";
    return 0;
  }
  slotEl.style.minHeight = `${next}px`;
  return next;
}

export function readHeroFlowHeight(el) {
  return asNumber(el?.offsetHeight);
}

/**
 * Keep document position stable when the hero height changes.
 * Pass `before - after`: shrink raises scrollTop, expand lowers it.
 */
export function compensateScrollTop(scroller, layoutDelta) {
  const delta = asNumber(layoutDelta);
  if (!delta) return 0;

  if (typeof window === "undefined") return 0;

  if (!scroller || scroller === window) {
    const y = asNumber(window.scrollY || document.documentElement?.scrollTop);
    window.scrollTo(0, Math.max(0, y + delta));
    return delta;
  }

  scroller.scrollTop = Math.max(0, asNumber(scroller.scrollTop) + delta);
  return delta;
}

export function readScrollerMetrics(scroller) {
  if (typeof window === "undefined") {
    return { scrollY: 0, maxScroll: 0 };
  }

  if (!scroller || scroller === window) {
    const root = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY || root?.scrollTop || 0;
    const scrollHeight = Math.max(
      root?.scrollHeight || 0,
      body?.scrollHeight || 0
    );
    const clientHeight = root?.clientHeight || window.innerHeight || 0;
    return {
      scrollY: asNumber(scrollY),
      maxScroll: Math.max(0, scrollHeight - clientHeight),
    };
  }

  return {
    scrollY: asNumber(scroller.scrollTop),
    maxScroll: Math.max(
      0,
      asNumber(scroller.scrollHeight) - asNumber(scroller.clientHeight)
    ),
  };
}

/**
 * overflow-x: clip|hidden computes a scroll container even when overflow-y is
 * visible/clip/hidden. Treat those as candidates; prefer ones that overflow.
 */
export function isVerticalScrollContainer(style) {
  if (!style) return false;
  const y = String(style.overflowY || "").toLowerCase();
  const x = String(style.overflowX || "").toLowerCase();
  if (y === "auto" || y === "scroll" || y === "overlay") return true;
  if (y === "hidden") return true;
  if (
    (x === "clip" || x === "hidden" || x === "auto" || x === "scroll" || x === "overlay") &&
    (y === "visible" || y === "clip" || y === "hidden" || y === "")
  ) {
    return true;
  }
  return false;
}

export function hasVerticalOverflow(node) {
  if (!node || node === window) return false;
  return asNumber(node.scrollHeight) > asNumber(node.clientHeight) + 1;
}

function readOverflowStyle(node) {
  if (typeof getComputedStyle !== "function") {
    return { overflowY: "", overflowX: "" };
  }
  try {
    return getComputedStyle(node);
  } catch {
    return { overflowY: "", overflowX: "" };
  }
}

export function scrollParentOf(el) {
  if (typeof document === "undefined") {
    return typeof window === "undefined" ? null : window;
  }

  let node = el?.parentElement;

  while (node && node !== document.body && node !== document.documentElement) {
    if (
      isVerticalScrollContainer(readOverflowStyle(node)) &&
      hasVerticalOverflow(node)
    ) {
      return node;
    }
    node = node.parentElement;
  }

  return typeof window === "undefined" ? null : window;
}
